import DatePicker from '@rio-cloud/rio-uikit/DatePicker';
import ExpanderPanel from '@rio-cloud/rio-uikit/ExpanderPanel';
import Select, { type SelectOption } from '@rio-cloud/rio-uikit/Select';
import SimpleTooltip from '@rio-cloud/rio-uikit/SimpleTooltip';
import classNames from 'classnames';
import type { Moment } from 'moment';
import type React from 'react';
import { useEffect, useState } from 'react';
import { FormattedMessage, type IntlShape, useIntl } from 'react-intl';
import { FeatureToggles, useToggle } from '../../../../../configuration/featureToggle/toggleHooks';
import { useLazyGetBrandQuery } from '../../../../services/assetsApi';
import FormInputField from '../FormInputField';
import { type Tachograph, FormInputFieldType, FuelType, OwnershipType } from '../types';
import { CountryCodeAutoSuggest } from './CountryCodeAutoSuggest';
import {
    endDateIsSameOrAfterStartDateOrNull,
    isAssetIdentificationValid,
    isAssetNameValid,
    isVinValid,
} from './assetDetailsHelper';
import { getIsLatamTenant } from '../../../../../configuration/login/loginSlice';
import { type AssetDetailsProperties, DeviceType } from './types';
import { useSelector } from 'react-redux';
import Rioglyph, { type IconType } from '@rio-cloud/rio-uikit/Rioglyph';
import { getFuelIcon } from '@rio-cloud/rio-uikit/fuelTypeUtils';

export const DATE_FORMAT = 'YYYY-MM-DD';
const NUMBER_OF_AXES_MAX_VALUE = 100;
const NUMBER_OF_AXES_MIN_VALUE = 0;

export const AssetDetails = (props: AssetDetailsProperties) => {
    const {
        asset,
        currentAssetIdentification,
        currentAssetBrand,
        currentAssetLicensePlate,
        currentAssetLicensePlateCountryCode,
        currentAssetName,
        currentMasterdataAdr,
        currentMasterdataAirFreightSuitability,
        currentMasterdataComment,
        currentMasterdataCustomsBond,
        currentMasterdataCostCenter,
        currentMasterdataFuelType,
        currentMasterdataLeasingRate,
        currentMasterdataNumberOfAxes,
        currentMasterdataOwnershipEndDate,
        currentMasterdataOwnershipEndDateChangeJustTriggered,
        currentMasterdataOwnershipStartDate,
        currentMasterdataOwnershipTermInMonths,
        currentMasterdataOwnershipTermInMonthsChangeJustTriggered,
        currentMasterdataOwnershipType,
        currentMasterdataReference,
        currentMasterdataRegisteredFirstAt,
        currentMasterdataTires,
        currentMasterdataVehicleModel,
        currentMasterdataEngineType,
        identificationHasError,
        locale,
        onAssetIdentificationChange,
        onAssetBrandChange,
        onAssetLicensePlateChange,
        onAssetLicensePlateCountryCodeChange,
        onAssetNameChange,
        onMasterdataAdr,
        onMasterdataAirFreightSuitability,
        onMasterdataComment,
        onMasterdataCustomsBond,
        onMasterdataCostCenter,
        onMasterdataFuelType,
        onMasterdataLeasingRate,
        onMasterdataNumberOfAxes,
        onMasterdataOwnershipType,
        onMasterdataOwnershipEndDate,
        onMasterdataOwnershipStartDate,
        onMasterdataReference,
        onMasterdataRegisteredFirstAt,
        onMasterdataOwnershipTermInMonths,
        onMasterdataTires,
        onMasterdataEngineType,
        onMasterdataVehicleModel,
        setIdentificationHasError,
    } = props;

    const intl = useIntl();

    const isLatamTenant = useSelector(getIsLatamTenant);

    const [triggerGetBrand, brandResult] = useLazyGetBrandQuery();
    const { value: featureEngineTypeVehicleModelIsEnabled } = useToggle(
        FeatureToggles.MASTERDATA_ENGINE_VEHICLE,
        false
    );
    const [isBasicInformationOpen, setIsBasicInformationOpen] = useState(true);
    const [isExtendedInformationOpen, setIsExtendedInformationOpen] = useState(false);

    const onAssetNameValueChange = (value: string | null) => {
        onAssetNameChange(value ?? '');
    };

    const onAssetLicensePlateValueChange = (value: string | null) => {
        onAssetLicensePlateChange(value);
    };

    const onIdentificationChange = (vinValue: string | null) => {
        const upperCaseValue = vinValue?.toUpperCase() ?? null;
        setIdentificationHasError(!isAssetIdentificationValid(upperCaseValue));
        onAssetIdentificationChange(upperCaseValue);
        if (upperCaseValue && isVinValid(upperCaseValue)) {
            triggerGetBrand({ vin: upperCaseValue });
        } else if (upperCaseValue && !isVinValid(upperCaseValue)) {
            onAssetBrandChange(null);
        }
    };

    useEffect(() => {
        if (brandResult.isSuccess && brandResult.currentData) {
            onAssetBrandChange(brandResult.currentData);
        } else if (brandResult.isLoading || brandResult.isError) {
            onAssetBrandChange(null);
        }
    }, [brandResult, onAssetBrandChange]);

    const isFuelTypeEditionDisabled = (): boolean => {
        return currentAssetBrand === 'MAN' && hasCm4Device();
    };

    const isFuelTypeApplicable = (fuelType: FuelType): boolean => FuelType.CNG !== fuelType || isLatamTenant === true;

    const hasCm4Device = () =>
        props.associatedDevices[asset.id]?.some(association =>
            [DeviceType.CM4, DeviceType.CM4_DEV].includes(association.device.type)
        );

    const getTachographValue = (tachograph: Tachograph | undefined): string => {
        if (!tachograph) {
            return '';
        }

        return [tachograph.brand, tachograph.model]
            .filter((value): value is string => Boolean(value?.trim()))
            .join(' - ');
    };

    const basicInformationBody = (
        <>
            <FormInputField
                translationId={'intl-msg:asset.name'}
                currentValue={currentAssetName ?? ''}
                disabled={false}
                onValueChange={onAssetNameValueChange}
                dataAttribute='detail-view-form-name'
                withCopyButton={true}
                maxLength={100}
                hasError={!isAssetNameValid(currentAssetName)}
            />
            <FormInputField
                translationId={'intl-msg:asset.identification_vin'}
                currentValue={currentAssetIdentification ?? ''}
                hasError={identificationHasError}
                hasFeedback={identificationHasError}
                disabled={!!asset.identification}
                onValueChange={onIdentificationChange}
                dataAttribute='detail-view-form-identification'
                hasLengthValidation={true}
                maxLength={asset.identification ? undefined : 17}
                withCopyButton={!!asset.identification}
            />
            <div className='row'>
                <div className='col-12 col-md-6'>
                    <FormInputField
                        translationId={'intl-msg:asset.brand'}
                        currentValue={currentAssetBrand ?? ''}
                        disabled={true}
                        dataAttribute='detail-view-form-brand'
                    />
                </div>
                <div className='col-12 col-md-6'>
                    <FormInputField
                        translationId={'intl-msg:asset.license-plate'}
                        currentValue={currentAssetLicensePlate ?? ''}
                        disabled={false}
                        onValueChange={onAssetLicensePlateValueChange}
                        dataAttribute='detail-view-form-license-plate'
                        withCopyButton={true}
                        maxLength={100}
                    />
                </div>
            </div>
            {featureEngineTypeVehicleModelIsEnabled ? (
                <>
                    <div className='row'>
                        <div className='col-12 col-md-6'>
                            <FormInputField
                                translationId={'intl-msg:asset.type'}
                                currentValue={intl.formatMessage({
                                    id: `intl-msg:asset.type.${asset.type.toLowerCase()}`,
                                })}
                                disabled={true}
                                dataAttribute='detail-view-form-type'
                                icon={<Rioglyph icon={`rioglyph-${asset.type.toLowerCase()}` as IconType} />}
                            />
                        </div>
                        <div className='col-12 col-md-6'>
                            <FormInputField
                                translationId={'intl-msg:masterdata.vehicle-model'}
                                currentValue={currentMasterdataVehicleModel ?? ''}
                                disabled={false}
                                onValueChange={onMasterdataVehicleModel}
                                dataAttribute='detail-view-form-vehicle-model'
                                withCopyButton={false}
                                maxLength={100}
                                hasError={false}
                            />
                        </div>
                    </div>
                    <div className='row'>
                        <div className='col-12 col-md-6'>
                            <SelectWrapper
                                formatMessageId={'intl-msg:masterdata.fuel-type'}
                                onSelect={it =>
                                    onMasterdataFuelType(FuelType[it.toUpperCase() as keyof typeof FuelType])
                                }
                                onClear={() => onMasterdataFuelType(null)}
                                value={currentMasterdataFuelType}
                                disabled={isFuelTypeEditionDisabled()}
                                tooltipMessageId={'intl-msg:masterdata.fuel-type.tooltip'}
                                options={Object.values(FuelType).filter(option => isFuelTypeApplicable(option))}
                                iconSelector={it => getFuelIcon(it.toLowerCase() as FuelType)}
                                intl={intl}
                            />
                        </div>
                        <div className='col-12 col-md-6'>
                            <FormInputField
                                translationId={'intl-msg:masterdata.engine-type'}
                                currentValue={currentMasterdataEngineType ?? ''}
                                disabled={false}
                                onValueChange={onMasterdataEngineType}
                                dataAttribute='detail-view-form-engine-type'
                                withCopyButton={false}
                                maxLength={100}
                                hasError={false}
                            />
                        </div>
                    </div>
                </>
            ) : (
                <>
                    <FormInputField
                        translationId={'intl-msg:asset.type'}
                        currentValue={intl.formatMessage({
                            id: `intl-msg:asset.type.${asset.type.toLowerCase()}`,
                        })}
                        disabled={true}
                        dataAttribute='detail-view-form-type'
                        icon={<Rioglyph icon={`rioglyph-${asset.type.toLowerCase()}` as IconType} />}
                    />
                    <SelectWrapper
                        formatMessageId={'intl-msg:masterdata.fuel-type'}
                        onSelect={it => onMasterdataFuelType(FuelType[it.toUpperCase() as keyof typeof FuelType])}
                        onClear={() => onMasterdataFuelType(null)}
                        value={currentMasterdataFuelType}
                        disabled={isFuelTypeEditionDisabled()}
                        tooltipMessageId={'intl-msg:masterdata.fuel-type.tooltip'}
                        options={Object.values(FuelType).filter(option => isFuelTypeApplicable(option))}
                        iconSelector={it => getFuelIcon(it.toLowerCase() as FuelType)}
                        intl={intl}
                    />
                </>
            )}
        </>
    );

    const extendedInformationBody = (
        <>
            <CountryCodeAutoSuggest
                assetId={asset.id}
                licensePlateCountryCode={currentAssetLicensePlateCountryCode}
                onSelectionChange={onAssetLicensePlateCountryCodeChange}
                locale={locale}
                hasError={false}
            />
            <FormInputField
                translationId={'intl-msg:masterdata.tachograph'}
                currentValue={getTachographValue(asset.masterdata?.tachograph)}
                disabled={true}
                dataAttribute='detail-view-form-tachograph'
                withCopyButton={false}
            />
            {asset.masterdata?.plug_and_charge_ids?.map((pcId, index, pcIds) => (
                <FormInputField
                    translationId={'intl-msg:asset.pcids'}
                    messageValues={{ index: pcIds.length > 1 ? `${index + 1}. ` : '' }}
                    currentValue={pcId.pc_id}
                    key={pcId.pc_id}
                    disabled={true}
                    dataAttribute={'detail-view-form-pcid'}
                    withCopyButton={true}
                />
            ))}
            <FormInputField
                translationId={'intl-msg:masterdata.adr'}
                currentValue={currentMasterdataAdr ?? ''}
                disabled={false}
                onValueChange={it => {
                    onMasterdataAdr(it);
                }}
                dataAttribute='detail-view-form-adr'
                withCopyButton={false}
                maxLength={100}
                hasError={false}
            />
            <FormInputField
                translationId={'intl-msg:masterdata.customs-bond'}
                currentValue={currentMasterdataCustomsBond ?? ''}
                disabled={false}
                onValueChange={it => {
                    onMasterdataCustomsBond(it);
                }}
                dataAttribute='detail-view-form-customs-bond'
                withCopyButton={false}
                maxLength={100}
                hasError={false}
            />

            <FormInputField
                translationId={'intl-msg:masterdata.cost-center'}
                currentValue={currentMasterdataCostCenter ?? ''}
                disabled={false}
                onValueChange={onMasterdataCostCenter}
                dataAttribute='detail-view-form-cost-center'
                withCopyButton={false}
                maxLength={100}
                hasError={false}
            />
            <FormInputField
                translationId={'intl-msg:masterdata.suitability-for-air-freight'}
                currentValue={currentMasterdataAirFreightSuitability ?? ''}
                disabled={false}
                onValueChange={it => {
                    onMasterdataAirFreightSuitability(it);
                }}
                dataAttribute='detail-view-form-air-freight'
                withCopyButton={false}
                maxLength={100}
                hasError={false}
            />
            <FormInputField
                translationId={'intl-msg:masterdata.tires'}
                currentValue={currentMasterdataTires ?? ''}
                disabled={false}
                onValueChange={it => {
                    onMasterdataTires(it);
                }}
                dataAttribute='detail-view-form-tires'
                withCopyButton={false}
                maxLength={100}
                hasError={false}
                type={FormInputFieldType.TEXTAREA}
            />
            <FormInputField
                translationId={'intl-msg:masterdata.number-of-axes'}
                currentValue={currentMasterdataNumberOfAxes?.toString() ?? ''}
                disabled={false}
                onValueChange={it => {
                    onMasterdataNumberOfAxes(
                        limitValue(getNumberOrNull(it), NUMBER_OF_AXES_MIN_VALUE, NUMBER_OF_AXES_MAX_VALUE)
                    );
                }}
                dataAttribute='detail-view-form-axes'
                withCopyButton={false}
                maxLength={100}
                hasError={false}
            />
            <FormInputField
                translationId={'intl-msg:masterdata.comment'}
                currentValue={currentMasterdataComment ?? ''}
                disabled={false}
                onValueChange={it => {
                    onMasterdataComment(it);
                }}
                dataAttribute='detail-view-form-comment'
                withCopyButton={false}
                maxLength={100}
                hasError={false}
                type={FormInputFieldType.TEXTAREA}
            />
            <div>
                <div className={'form-group'}>
                    <label>
                        <FormattedMessage id={'intl-msg:masterdata.date-of-first-registration'} />
                    </label>
                    <DatePickerWrapper
                        value={currentMasterdataRegisteredFirstAt}
                        onChange={onMasterdataRegisteredFirstAt}
                    />
                </div>
                <div className={'form-group'}>
                    <label>
                        <FormattedMessage id={'intl-msg:masterdata.ownership-start-date'} />
                    </label>
                    <DatePickerWrapper
                        value={currentMasterdataOwnershipStartDate}
                        onChange={onMasterdataOwnershipStartDate}
                    />
                </div>
                <div
                    className={`form-group ${currentMasterdataOwnershipEndDateChangeJustTriggered ? 'has-success' : ''}`}
                >
                    <label>
                        <FormattedMessage id={'intl-msg:masterdata.ownership-end-date'} />
                    </label>
                    <DatePickerWrapper
                        value={currentMasterdataOwnershipEndDate}
                        onChange={onMasterdataOwnershipEndDate}
                        hasError={
                            !endDateIsSameOrAfterStartDateOrNull(
                                currentMasterdataOwnershipStartDate,
                                currentMasterdataOwnershipEndDate
                            )
                        }
                        errorFormattedMessageId={'intl-msg:masterdata.select-date.error.end-date-before-start-date'}
                    />
                </div>
                <div className={currentMasterdataOwnershipTermInMonthsChangeJustTriggered ? 'has-success' : ''}>
                    <FormInputField
                        translationId={'intl-msg:masterdata.term-in-months'}
                        currentValue={currentMasterdataOwnershipTermInMonths?.toString() ?? ''}
                        disabled={false}
                        onValueChange={it => {
                            onMasterdataOwnershipTermInMonths(getNumberOrNull(it));
                        }}
                        dataAttribute='detail-view-form-term'
                        withCopyButton={false}
                        maxLength={100}
                        hasError={false}
                    />
                </div>
                <SelectWrapper
                    formatMessageId={'intl-msg:masterdata.ownership-type'}
                    onSelect={it =>
                        onMasterdataOwnershipType(OwnershipType[it?.toUpperCase() as keyof typeof OwnershipType])
                    }
                    onClear={() => onMasterdataOwnershipType(null)}
                    value={currentMasterdataOwnershipType}
                    options={Object.values(OwnershipType)}
                    intl={intl}
                />
                <FormInputField
                    translationId={'intl-msg:masterdata.reference'}
                    currentValue={currentMasterdataReference ?? ''}
                    disabled={false}
                    onValueChange={it => {
                        onMasterdataReference(it);
                    }}
                    dataAttribute='detail-view-form-reference'
                    withCopyButton={false}
                    maxLength={100}
                    hasError={false}
                />
                <FormInputField
                    translationId={'intl-msg:masterdata.leasing-rate'}
                    currentValue={currentMasterdataLeasingRate?.toString() ?? ''}
                    disabled={false}
                    onValueChange={it => {
                        onMasterdataLeasingRate(getNumberOrNull(it));
                    }}
                    dataAttribute='detail-view-form-leasing-rate'
                    withCopyButton={false}
                    maxLength={100}
                    hasError={false}
                />
            </div>
        </>
    );

    return (
        <form data-cy='detail-view-form'>
            <div className='padding-bottom-20'>
                <ExpanderPanel
                    title={<FormattedMessage id={'intl-msg:asset.basic-information'} />}
                    bsStyle='default'
                    className='shadow-subtle margin-bottom-10'
                    bodyClassName='padding-bottom-10'
                    open={isBasicInformationOpen}
                    onToggle={setIsBasicInformationOpen}
                >
                    {basicInformationBody}
                </ExpanderPanel>
                <ExpanderPanel
                    title={<FormattedMessage id={'intl-msg:asset.extended-information'} />}
                    bsStyle='default'
                    className='shadow-subtle'
                    bodyClassName='padding-bottom-10'
                    open={isExtendedInformationOpen}
                    onToggle={setIsExtendedInformationOpen}
                >
                    {extendedInformationBody}
                </ExpanderPanel>
            </div>
        </form>
    );
};

function SelectWrapper(props: {
    formatMessageId: string;
    onSelect: (value: string) => void;
    onClear: () => void;
    value: string | null | undefined;
    options: string[];
    intl: IntlShape;
    tooltipMessageId?: string | undefined;
    disabled?: boolean;
    iconSelector?: (type: string) => IconType;
}) {
    const select = (
        <Select
            id={props.formatMessageId}
            disabled={props.disabled}
            className={'form-group'}
            value={[props.value || '']}
            placeholder={props.intl.formatMessage({ id: props.formatMessageId })}
            options={props.options.map(it => ({
                id: it,
                label: props.intl.formatMessage({ id: `${props.formatMessageId}.${it}` }),
                icon: props.iconSelector && <Rioglyph icon={props.iconSelector(it)} />,
            }))}
            onChange={(selectedOption: SelectOption | undefined) => {
                if (selectedOption) {
                    props.onSelect(selectedOption.id);
                } else {
                    props.onClear();
                }
            }}
            useClear={true}
        />
    );

    return (
        <div>
            <label htmlFor={props.formatMessageId}>
                <FormattedMessage id={props.formatMessageId} />
            </label>
            {props.disabled && props.tooltipMessageId ? (
                <SimpleTooltip
                    content={
                        <FormattedMessage
                            id={props.tooltipMessageId}
                            values={{
                                man: 'MAN',
                            }}
                        />
                    }
                    placement={'left'}
                >
                    <div>{select}</div>
                </SimpleTooltip>
            ) : (
                select
            )}
        </div>
    );
}

const DatePickerWrapper = (
    props: React.PropsWithChildren<{
        onChange: (value: string | undefined) => void;
        value: string | undefined;
        hasError?: boolean;
        errorFormattedMessageId?: string;
    }>
) => {
    const intl = useIntl();
    return (
        <>
            <DatePicker
                onChange={(value: string | Moment | undefined) => {
                    if (value && typeof value === 'object') {
                        props.onChange(value.format(DATE_FORMAT));
                    } else {
                        props.onChange(undefined);
                    }
                }}
                value={props.value}
                closeOnSelect={true}
                dateFormat={'YYYY-MM-DD'}
                inputProps={{
                    value: props.value === null || props.value === undefined ? '' : props.value,
                    placeholder: intl.formatMessage({ id: 'intl-msg:masterdata.select-date' }),
                }}
                timeFormat={false}
                clearableInput={true}
                className={classNames(props.hasError ? 'has-error' : '')}
                mandatory={false}
            />
            {props.hasError === true && (
                <p className={classNames('text-danger', 'text-size-12')}>
                    <FormattedMessage id={props.errorFormattedMessageId} />
                </p>
            )}
        </>
    );
};

const limitValue = (value: number | null, minValue: number, maxValue: number) => {
    return value ? Math.max(Math.min(value, maxValue), minValue) : null;
};

const getNumberOrNull = (it: string | null): number | null => {
    const parsedInt = Number.parseInt(it ?? '', 10);
    return Number.isNaN(parsedInt) ? null : parsedInt;
};

AssetDetails.headingText = <FormattedMessage id={'intl-msg:asset.basic-asset-data'} />;
